import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronLeft,
  ShoppingBag,
  CheckCircle,
  CreditCard,
  Banknote,
  Loader2,
  User,
  ChevronDown,
  Pencil,
  Sparkles
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useCartStore } from '../stores/cartStore'
import { calculateCustomizedPrice } from '../types'
import { getTheme } from '@/config/menuThemes'
import { MenuThemeProvider, useMenuTheme } from '@/contexts/MenuThemeContext'

interface AvailablePaymentMethods {
  availableMethods: string[]
  defaultMethod: string
  stripePublishableKey?: string
}

function CheckoutContent() {
  const navigate = useNavigate()
  const { items, submitOrder, isLoading, error, sessionCode, itemCount, restaurantId, subtotal, total } = useCartStore()
  const { theme } = useMenuTheme()

  const [customerName, setCustomerName] = useState('')
  const [orderNotes, setOrderNotes] = useState('')
  const [showNotes, setShowNotes] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<AvailablePaymentMethods | null>(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('CASH')
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true)

  useEffect(() => {
    if (restaurantId) {
      loadAvailablePaymentMethods()
    }
  }, [restaurantId])

  const loadAvailablePaymentMethods = async () => {
    try {
      const response = await fetch(`/api/public/restaurants/${restaurantId}/available-payment-methods`)
      if (response.ok) {
        const data: AvailablePaymentMethods = await response.json()
        setPaymentMethods(data)
        setSelectedPaymentMethod(data.defaultMethod || 'CASH')
      }
    } catch (err) {
      console.error('Error loading payment methods:', err)
    } finally {
      setLoadingPaymentMethods(false)
    }
  }

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'STRIPE':
      case 'SATISPAY':
        return <CreditCard className="w-5 h-5" />
      case 'CASH':
        return <Banknote className="w-5 h-5" />
      default:
        return null
    }
  }

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'STRIPE':
        return 'Carta'
      case 'SATISPAY':
        return 'Satispay'
      case 'CASH':
        return 'Contanti'
      default:
        return method
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()

    if (items.length === 0 || !customerName.trim()) return

    try {
      const skipSubmit = selectedPaymentMethod === 'STRIPE' || selectedPaymentMethod === 'SATISPAY'
      let paymentMethod = 'CASH'
      if (selectedPaymentMethod === 'STRIPE' || selectedPaymentMethod === 'SATISPAY') {
        paymentMethod = 'CARD'
      }

      const order = await submitOrder(orderNotes, customerName.trim(), skipSubmit, paymentMethod)
      setShowSuccess(true)

      setTimeout(() => {
        if (selectedPaymentMethod === 'STRIPE') {
          navigate(`/payment?orderId=${order.id}`)
        } else if (selectedPaymentMethod === 'SATISPAY') {
          navigate(`/payment/satispay?orderId=${order.id}`)
        } else {
          navigate(`/order-tracking/${order.id}`)
        }
      }, 1500)
    } catch (err) {
      console.error('Errore durante l\'invio dell\'ordine:', err)
    }
  }

  const count = itemCount()
  const subtotalValue = subtotal()
  const totalValue = total()

  // Success State
  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl"
            style={{ boxShadow: `0 25px 50px -12px rgba(16, 185, 129, 0.4)` }}
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>
          <h2 className="text-3xl font-bold mb-3" style={{ color: theme.textColor }}>
            Ordine inviato!
          </h2>
          <p style={{ color: theme.textSecondary }} className="text-lg">
            Stai per essere reindirizzato...
          </p>
        </motion.div>
      </div>
    )
  }

  // Invalid Session State
  if (!sessionCode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-8 max-w-md w-full text-center"
          style={{ backgroundColor: theme.cardBackground, borderColor: theme.borderColor, borderWidth: '1px' }}
        >
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.textColor }}>
            Sessione non valida
          </h2>
          <p className="mb-6" style={{ color: theme.textSecondary }}>
            Per effettuare un ordine, scansiona il QR code al tuo tavolo.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 text-white font-semibold rounded-xl transition-all"
            style={{ background: theme.buttonGradient }}
          >
            Torna alla home
          </button>
        </motion.div>
      </div>
    )
  }

  // Empty Cart State
  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.backgroundColor }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-8 max-w-md w-full text-center"
          style={{ backgroundColor: theme.cardBackground, borderColor: theme.borderColor, borderWidth: '1px' }}
        >
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${theme.textColor}0d` }}>
            <ShoppingBag className="w-10 h-10" style={{ color: theme.mutedColor }} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.textColor }}>
            Carrello vuoto
          </h2>
          <p className="mb-6" style={{ color: theme.textSecondary }}>
            Aggiungi qualcosa di delizioso dal menu!
          </p>
          <button
            onClick={() => navigate(-1)}
            className="w-full py-3 text-white font-semibold rounded-xl transition-all"
            style={{ background: theme.buttonGradient }}
          >
            Esplora il Menu
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.backgroundColor }}>
      {/* Compact Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl" style={{ backgroundColor: `${theme.backgroundColor}f0` }}>
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ backgroundColor: `${theme.textColor}0d` }}
          >
            <ChevronLeft className="w-5 h-5" style={{ color: theme.textColor }} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold" style={{ color: theme.textColor }}>Checkout</h1>
            <p className="text-xs" style={{ color: theme.mutedColor }}>{count} {count === 1 ? 'articolo' : 'articoli'}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold" style={{ color: theme.primaryColor }}>€{totalValue.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-4 pb-36 space-y-4">

        {/* Order Items - Compact horizontal scroll */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {items.map((item, index) => {
              const unitPrice = calculateCustomizedPrice(item.price, item.customizations ?? null)
              const itemTotal = unitPrice * item.quantity
              return (
                <motion.div
                  key={`${item.productId}-${index}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="shrink-0 w-24 text-center"
                >
                  <div className="relative">
                    <div
                      className="w-24 h-24 rounded-2xl overflow-hidden mb-2"
                      style={{ backgroundColor: `${theme.textColor}0d` }}
                    >
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl">🍽️</div>
                      )}
                    </div>
                    {item.quantity > 1 && (
                      <div
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: theme.primaryColor }}
                      >
                        {item.quantity}
                      </div>
                    )}
                  </div>
                  <p className="text-xs font-medium truncate" style={{ color: theme.textColor }}>{item.name}</p>
                  <p className="text-xs font-semibold" style={{ color: theme.primaryColor }}>€{itemTotal.toFixed(2)}</p>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Name Input - Clean design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl p-4"
          style={{ backgroundColor: theme.cardBackground, borderColor: theme.borderColor, borderWidth: '1px' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${theme.primaryColor}20` }}
            >
              <User className="w-6 h-6" style={{ color: theme.primaryColor }} />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium mb-1 block" style={{ color: theme.mutedColor }}>
                Il tuo nome
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Come ti chiami?"
                className="w-full bg-transparent text-base font-medium focus:outline-none"
                style={{ color: theme.textColor }}
              />
            </div>
          </div>
        </motion.div>

        {/* Notes - Collapsible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-2 py-2 transition-colors"
            style={{ color: theme.mutedColor }}
          >
            <Pencil size={14} />
            <span className="text-sm">
              {orderNotes ? 'Modifica note' : 'Aggiungi note per la cucina'}
            </span>
            <motion.div
              animate={{ rotate: showNotes ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={14} />
            </motion.div>
          </button>

          <AnimatePresence>
            {showNotes && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Es. Allergie, preferenze..."
                  rows={2}
                  autoFocus
                  className="w-full mt-2 px-4 py-3 rounded-xl text-sm resize-none focus:outline-none transition-colors"
                  style={{
                    backgroundColor: theme.cardBackground,
                    borderColor: theme.borderColor,
                    borderWidth: '1px',
                    color: theme.textColor,
                  }}
                  maxLength={200}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {!showNotes && orderNotes && (
            <p className="text-xs mt-1 truncate" style={{ color: theme.mutedColor }}>"{orderNotes}"</p>
          )}
        </motion.div>

        {/* Payment Methods - Modern pills */}
        {!loadingPaymentMethods && paymentMethods && paymentMethods.availableMethods.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-4"
            style={{ backgroundColor: theme.cardBackground, borderColor: theme.borderColor, borderWidth: '1px' }}
          >
            <p className="text-xs font-medium mb-3" style={{ color: theme.mutedColor }}>
              Metodo di pagamento
            </p>
            <div className="flex gap-2">
              {paymentMethods.availableMethods.map((method) => (
                <button
                  key={method}
                  onClick={() => setSelectedPaymentMethod(method)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl transition-all"
                  style={{
                    backgroundColor: selectedPaymentMethod === method ? `${theme.primaryColor}20` : `${theme.textColor}08`,
                    borderColor: selectedPaymentMethod === method ? theme.primaryColor : 'transparent',
                    borderWidth: '2px',
                    color: selectedPaymentMethod === method ? theme.primaryColor : theme.textSecondary
                  }}
                >
                  {getPaymentMethodIcon(method)}
                  <span className="font-medium text-sm">{getPaymentMethodLabel(method)}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Summary - Minimal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-2xl p-4"
          style={{ backgroundColor: theme.cardBackground, borderColor: theme.borderColor, borderWidth: '1px' }}
        >
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span style={{ color: theme.textSecondary }}>Subtotale</span>
              <span style={{ color: theme.textColor }}>€{subtotalValue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: theme.textSecondary }}>IVA (10%)</span>
              <span style={{ color: theme.textColor }}>€{(subtotalValue * 0.10).toFixed(2)}</span>
            </div>
          </div>
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl"
            >
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Fixed Bottom - Premium CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 z-20"
        style={{
          backgroundColor: theme.backgroundColor,
          borderTopColor: theme.borderColor,
          borderTopWidth: '1px'
        }}
      >
        <div className="max-w-lg mx-auto">
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleSubmit}
            disabled={isLoading || !customerName.trim()}
            className="w-full py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 transition-all text-white disabled:opacity-40"
            style={{
              background: customerName.trim() ? theme.buttonGradient : `${theme.textColor}1a`,
              boxShadow: customerName.trim() ? `0 20px 40px -10px ${theme.shadowColor}` : 'none'
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Invio in corso...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>Conferma • €{totalValue.toFixed(2)}</span>
              </>
            )}
          </motion.button>

          {/* Payment method hint */}
          <p className="text-center text-xs mt-3" style={{ color: theme.mutedColor }}>
            {selectedPaymentMethod === 'CASH' ? '💰 Pagherai in contanti al ritiro' : '💳 Pagamento sicuro online'}
          </p>
        </div>
      </div>

      {/* Scrollbar hide */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}

export const CheckoutPage: React.FC = () => {
  const { menuTheme } = useCartStore()
  const theme = getTheme(menuTheme || undefined)

  return (
    <MenuThemeProvider theme={theme}>
      <CheckoutContent />
    </MenuThemeProvider>
  )
}
